/**
 * JDMatchScreen.jsx — Premium JD Match Center
 * Clean input → results flow with keyword columns
 * State is passed from parent (hoisted in ChatPage to avoid hooks-in-IIFE bug)
 */
import React from 'react';
import {
  Target, Zap, Plus, CheckCircle, XCircle,
  Loader, FileText, AlertCircle, ClipboardPaste,
} from 'lucide-react';

/* ── Score ring ── */
function MatchRing({ score = 0 }) {
  const R = 38;
  const circ = 2 * Math.PI * R;
  const pct = Math.min(100, Math.max(0, score));
  const offset = circ - (pct / 100) * circ;
  const color = pct >= 75 ? '#059669' : pct >= 55 ? '#D97706' : '#DC2626';
  const label = pct >= 75 ? 'Strong' : pct >= 55 ? 'Fair' : 'Weak';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ position: 'relative', width: 96, height: 96 }}>
        <svg width={96} height={96} viewBox="0 0 96 96" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={48} cy={48} r={R} fill="none" stroke="var(--card-border)" strokeWidth={7} />
          <circle
            cx={48} cy={48} r={R} fill="none"
            stroke={color} strokeWidth={7}
            strokeDasharray={circ} strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.7s cubic-bezier(0.16,1,0.3,1)' }}
          />
        </svg>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: 22, fontWeight: 900, color: 'var(--text-primary)', lineHeight: 1 }}>{score}%</span>
        </div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color }}>{label} Match</span>
    </div>
  );
}

/* ── Keyword column ── */
function KeywordCol({ title, keywords = [], variant }) {
  const isMatch = variant === 'match';
  return (
    <div style={{ flex: 1 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10,
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
        color: isMatch ? 'var(--status-success)' : 'var(--status-danger)',
      }}>
        {isMatch ? <CheckCircle size={12} /> : <XCircle size={12} />}
        {title} ({keywords.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {keywords.slice(0, 12).map((kw, i) => (
          <span key={i} className={isMatch ? 'kw-tag-match' : 'kw-tag-missing'}
            style={{ width: 'fit-content' }}>
            {typeof kw === 'string' ? kw : kw?.keyword || kw?.text || String(kw)}
          </span>
        ))}
        {keywords.length > 12 && (
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{keywords.length - 12} more</span>
        )}
      </div>
    </div>
  );
}

export default function JDMatchScreen({
  // State (hoisted in ChatPage)
  jdText, setJdText,
  jdResult, setJdResult,
  jdLoading,
  jdError, setJdError,
  varCreating,
  varCreated,
  deepAnalysis,
  setDeepAnalysis,
  // Handlers
  onAnalyze,
  onOptimize,
  onCreateVariant,
  onCoverLetter,
  theme = 'dark',
}) {
  const T = theme === 'dark';
  const isMobile = window.innerWidth < 768;

  const matchScore  = jdResult?.match_score ?? jdResult?.score ?? 0;
  const matched     = jdResult?.matched_keywords ?? jdResult?.matched_skills ?? [];
  const missing     = jdResult?.missing_keywords ?? jdResult?.gap_skills ?? [];
  const suggestions = jdResult?.suggestions ?? [];

  return (
    <div 
      className="flex-1 h-full overflow-y-auto md:overflow-y-hidden px-4 pb-20 pt-[76px] md:px-6 md:pb-6 md:pt-[84px]"
      style={{
        fontFamily: "'Inter', sans-serif",
        background: T ? '#0b0b14' : '#F8FAFC',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* Header */}
      <div style={{ flexShrink: 0 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 2px' }}>
          JD Match Center
        </h2>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
          Compare your resume against any job description to discover critical keyword and experience gaps.
        </p>
      </div>

      {/* Two Column Container */}
      <div className="flex flex-col md:flex-row gap-6 min-h-0 overflow-visible md:overflow-hidden flex-none md:flex-1">
        
        {/* Left Column: Job Description Input */}
        <div className="flex-none md:flex-[1_1_45%] flex flex-col gap-[14px] overflow-y-visible md:overflow-y-auto pr-0 md:pr-1">
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12, padding: 16 }}>
            {/* Pricing Segmented Switcher */}
            <div style={{
              display: 'flex', background: 'var(--surface-1)', border: '1px solid var(--card-border)',
              borderRadius: 8, padding: 3, marginBottom: 12, gap: 4
            }}>
              <button
                onClick={() => setDeepAnalysis(false)}
                style={{
                  flex: 1, padding: '6px 12px', fontSize: 11, fontWeight: 700, borderRadius: 6,
                  background: !deepAnalysis ? '#18181b' : 'transparent',
                  color: !deepAnalysis ? '#ffffff' : 'var(--text-muted)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s'
                }}
              >
                Standard Match (2 Credits)
              </button>
              <button
                onClick={() => setDeepAnalysis(true)}
                style={{
                  flex: 1, padding: '6px 12px', fontSize: 11, fontWeight: 700, borderRadius: 6,
                  background: deepAnalysis ? '#18181b' : 'transparent',
                  color: deepAnalysis ? '#ffffff' : 'var(--text-muted)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s'
                }}
              >
                Deep AI Analysis (5 Credits)
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
              <ClipboardPaste size={14} color="var(--text-muted)" style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                Job Description Text
              </span>
            </div>
            <textarea
              className="p-input"
              rows={8}
              placeholder="Paste the full job description here — include requirements, qualifications, and responsibilities..."
              value={jdText}
              onChange={e => { setJdText(e.target.value); setJdError && setJdError(''); }}
              style={{ minHeight: 180, width: '100%' }}
            />
            {jdError && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
                <AlertCircle size={12} color="var(--status-danger)" />
                <span style={{ fontSize: 11, color: 'var(--status-danger)' }}>{jdError}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                {jdText.length} / 5000 characters
              </span>
              <button
                className="btn-primary"
                onClick={onAnalyze}
                disabled={jdLoading || jdText.trim().length < 50}
                style={{ fontSize: 11, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {jdLoading
                  ? <><Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> Analyzing...</>
                  : <><Target size={12} /> {deepAnalysis ? 'Run Deep AI Match' : 'Run Standard Match'}</>
                }
              </button>
            </div>
          </div>
        </div>

        {/* Vertical Divider */}
        <div className="hidden md:block w-[1px] bg-[var(--card-border)] self-stretch" />

        {/* Right Column: Match Results or Placeholder */}
        <div className="flex-none md:flex-[1_1_55%] flex flex-col gap-[14px] overflow-y-visible md:overflow-y-auto pl-0 md:pl-1">
          {jdLoading && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifycontent: 'center', gap: 12, color: 'var(--text-muted)', paddingTop: 40 }}>
              <Loader size={36} className="animate-spin" style={{ animation: 'spin 1.5s linear infinite', color: 'var(--brand)' }} />
              <p style={{ fontSize: 13, fontWeight: 600 }}>Analyzing match semantics...</p>
            </div>
          )}

          {!jdLoading && !jdResult && (
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12, padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ padding: 8, borderRadius: 8, background: 'var(--brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Target size={20} style={{ color: 'var(--brand)' }} />
                </div>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                  Job Description Match Analysis
                </h3>
              </div>
              
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: '1.5', margin: 0 }}>
                Paste the job requirements on the left. We will perform semantic matching and compute:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
                {[
                  { text: 'Skills Match', desc: 'Identify stack overlaps' },
                  { text: 'ATS Score', desc: 'Verify parsing success' },
                  { text: 'Missing Keywords', desc: 'Find vocabulary gaps' },
                  { text: 'Missing Experience', desc: 'Verify role durations' },
                  { text: 'Missing Certifications', desc: 'Verify credential requirements' },
                  { text: 'AI Suggestions', desc: 'Get actionable optimizations' }
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{ color: 'var(--status-success)', fontWeight: 'bold', fontSize: 13 }}>✓</span>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{item.text}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!jdLoading && jdResult && (
            <>
              {/* Score + keywords */}
              <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12, padding: 16 }}>
                <div className="flex flex-col md:flex-row gap-5 items-center md:items-start">
                  <MatchRing score={matchScore} />
                  <div style={{ flex: 1, width: '100%' }}>
                    <div className="flex flex-col md:flex-row gap-4">
                      <KeywordCol title="Matched" keywords={matched} variant="match" />
                      <KeywordCol title="Missing" keywords={missing} variant="missing" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Years of Experience & Certifications Card */}
              <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p style={{ fontSize: 10, fontWeight: 750, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', margin: 0 }}>
                  Requirements Verification
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Experience check */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>Years of Experience</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: jdResult.years_match ? 'var(--status-success)' : 'var(--status-danger)' }}>
                      {jdResult.years_actual} yrs actual vs {jdResult.years_required} yrs required
                    </span>
                  </div>
                  {/* Certifications check */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>Missing Certifications</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: (jdResult.missing_certifications || []).length === 0 ? 'var(--status-success)' : 'var(--status-danger)' }}>
                      {(jdResult.missing_certifications || []).length === 0 ? 'None missing' : jdResult.missing_certifications.join(', ').toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12, padding: 16 }}>
                  <p style={{ fontSize: 10, fontWeight: 750, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', margin: '0 0 10px' }}>
                    {jdResult.deep_analysis ? 'AI Gap & Improvement Analysis' : 'Keyword Match Suggestions'}
                  </p>
                  {suggestions.slice(0, 5).map((s, i) => {
                    const titleText = typeof s === 'string' ? s : s?.title || s?.text || s?.message;
                    const bodyText = typeof s === 'string' ? '' : s?.suggestion || '';
                    const gain = typeof s === 'string' ? null : s?.estimated_gain;
                    return (
                      <div key={i} style={{
                        display: 'flex', gap: 8, padding: '8px 0',
                        borderBottom: i < Math.min(suggestions.length - 1, 4) ? '1px solid var(--card-border)' : 'none',
                      }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                          background: 'var(--brand-light)', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: 9, fontWeight: 800, color: 'var(--brand-text)',
                          marginTop: 2
                        }}>
                          {i + 1}
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.4 }}>
                            {titleText}
                          </span>
                          {bodyText && (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                              {bodyText}
                            </span>
                          )}
                        </div>
                        {gain !== null && gain !== undefined && (
                          <span style={{
                            fontSize: 9, fontWeight: 700, color: '#10B981',
                            background: 'rgba(16, 185, 129, 0.1)', padding: '2px 6px',
                            borderRadius: 4, height: 'fit-content', shrink: 0
                          }}>
                            +{gain} ATS
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button className="btn-primary" onClick={onOptimize} style={{ width: '100%', fontSize: 11, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Zap size={13} />
                  Optimize Resume for this JD
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <button
                    className="btn-secondary"
                    onClick={onCreateVariant}
                    disabled={varCreating || varCreated}
                    style={{ fontSize: 11, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  >
                    {varCreating
                      ? <><Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> Creating...</>
                      : varCreated
                      ? <><CheckCircle size={12} /> Version Created</>
                      : <><Plus size={12} /> Create Tailored Version</>
                    }
                  </button>
                  <button className="btn-secondary" onClick={onCoverLetter} style={{ fontSize: 11, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <FileText size={12} />
                    Generate Cover Letter
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
